import { Cureus } from '../../../../Cureus';
import { channelMarkerElement, markerByColor, markerByChannelType, selectedChannelIcon } from './Channels/MarkersAndIcons';

export const createMarker = ({ channel, map, markerColor, markerElement, options = {} }) => {
  
  const icon = markerIcon({channel, markerColor});
  
  const marker = window.L.marker(channel.coordinates, {icon: icon})  
  .bindPopup(markerElement, {
    minWidth: 380,
    minHeight: 90,
    closeOnClick: false,
  })
  addEventListeners(marker, channel, options);
  marker.addTo(map);
  return marker;
}

const markerIcon = ({channel, markerColor}) => {
  return markerColor ? markerByColor(markerColor) : markerByChannelType(channel);
}

export const addEventListeners = (marker, channel, options = {}) => {
  const {
    selectedChannelRef,
    allowSelectingChannel,
    userId,
  } = options;

  // need to be able to access channel from marker, used in subsequent functions
  marker.channel = channel

  marker.on('popupopen', () => {    
    options.marker = marker;
    options.channel = channel;
    
    const noChannelSelectedOrSelectedChannelIsNotThisOne = !selectedChannelRef?.current?.id || selectedChannelRef.current.id !== marker.channel.id
    
    if (noChannelSelectedOrSelectedChannelIsNotThisOne) {
      const color = channelMarkerColorHighlighted(marker.channel);
      setMarkerIconBasedOnState(marker, selectedChannelRef, color);
    }
    
    setTimeout(() => {
      handleMarkerElementTitle(options);

      if (!allowSelectingChannel) {
        return;
      }

      handleUnselectChannelButton(options);

      if (markerAlreadySelected(marker, selectedChannelRef)) {
        return;
      }

      handleSelectChannelButton(options);
    }, 300);
  });

  marker.on('popupclose', () => {
    setMarkerIconBasedOnState(marker, selectedChannelRef);
  });
  
  marker.on('mouseover', () => {
    marker.openPopup();
    handleMouseOver(marker, selectedChannelRef);
  });

  marker.on('mouseout', () => {
    setMarkerIconBasedOnState(marker, selectedChannelRef);
  });

  marker.on('click', () => {
    marker.openPopup();
    fireAmplitudeEvent('clickChannelPin', marker.channel, userId);
  });
}

const setMarkerIconBasedOnState = (marker, selectedChannelRef, color=null) => {
  if (markerAlreadySelected(marker, selectedChannelRef)) {
    return; // Keep current icon if selected
  }
  const icon = markerIcon({channel: marker.channel, markerColor: color});
  marker.setIcon(icon);
};

const handleMouseOver = (marker, selectedChannelRef) => {
  const color = channelMarkerColorHighlighted(marker.channel);
  setMarkerIconBasedOnState(marker, selectedChannelRef, color);
}

const channelMarkerColorHighlighted = (channel) => {
  if (channel.type == "Channel::CommercialChannel") {
    return "red";
  } else {
    return "orange";
  }
}

const handleMarkerElementTitle = (options) => {
  const {
    channel,
    userId
  } = options;
  const markerElementTitle = cloneElementAndReplace(`.marker-element-title[data-channel-id="${channel.id}"]`, options);
  if (markerElementTitle) {
    // On tablet/mobile the first touch will highlight the link, and then the second touch will open the link
    // This is a workaround to prevent the highlight
    markerElementTitle.addEventListener("touchend", () => {
      window.open(`/channels/${channel.id}`, '_blank');
    });
    
    markerElementTitle.addEventListener('click', () => {
      fireAmplitudeEvent('clickChannelPage', channel, userId);
    });
  }      
}

const fireAmplitudeEvent = (event, channel, userId) => {
  new Cureus.Amplitude({
    event_type: event,
    channel_id: channel.id,
    channel_name: channel.title,
    source: window.location.pathname,
    from: 'map',
    user_id: userId
  });
}

const handleSelectChannelButton = (options) => {
  const {
    marker,
    channel,
    selectedChannelRef,
    selectedMarkerRef,
    allowSelectingChannel,
    mapInstanceRef,
    handleChooseChannel
  } = options;

  const selectChannelButton = cloneElementAndReplace('.select-channel-button', options);
  if (selectChannelButton) {
    selectChannelButton.addEventListener('click', () => {
      // if selected button is clicked, unselect the current channel and select the new one
      if (hasChannelAndMarkerBeenSelected(marker, selectedChannelRef, selectedMarkerRef)) {
        // remove the previously selected marker
        mapInstanceRef.current.removeLayer(selectedMarkerRef.current);
        // replace the previously selected marker with a default marker
        let markerElement = channelMarkerElement(selectedChannelRef.current, allowSelectingChannel);
        createMarker({ channel: selectedChannelRef.current, map: mapInstanceRef.current, markerElement, options });
        // set the selected marker to the new marker
        selectedMarkerRef.current = marker;
      }
      // select the just now selected channel and set popup to selected channel icon and set to blue color
      handleChooseChannel(channel);
      selectedMarkerRef.current = marker;
      marker.setIcon(markerByColor("blue"));

      changeMarkerPopupToSelectedChannelIcon(options);
    });
  }  
}

export const markerAlreadySelected = (marker, selectedChannelRef) => {
  return selectedChannelRef?.current?.id === marker.channel.id;
}

export const hasChannelAndMarkerBeenSelected = (marker, selectedChannelRef, selectedMarkerRef) => {
  return selectedChannelRef?.current?.id && selectedMarkerRef?.current && selectedMarkerRef.current.channel.id != marker.channel.id
}

export const changeMarkerPopupToSelectedChannelIcon = (options) => {
  const {
    marker, 
    channel,
  } = options;
  
  const selectedIcon = selectedChannelIcon(channel);
  updateMarkerPopup(marker, selectedIcon, 85, 85);

  setTimeout(() => {
    handleUnselectChannelButton(options);
  }, 300);
}

const handleUnselectChannelButton = (options) => {
    const {
      marker,
      channel,
      selectedMarkerRef,
      handleChooseChannel,
      mapInstanceRef,
      allowSelectingChannel
    } = options;

  const unselectButton = cloneElementAndReplace('.unselect-channel-button', options);
  if (unselectButton) {

    unselectButton.addEventListener('click', () => {
      handleChooseChannel({});
      selectedMarkerRef.current = null;
      mapInstanceRef.current.removeLayer(marker);
      const markerElement = channelMarkerElement(channel, allowSelectingChannel);
      createMarker({ channel: channel, map: mapInstanceRef.current, markerElement, options });
      marker.openPopup();     
    });
  }  
}

const cloneElementAndReplace = (htmlQuerySelector, options) => {
  if (!htmlQuerySelector) {
    console.warn('cloneElementAndReplace: No selector provided');
    return null;
  }

  const element = document.querySelector(htmlQuerySelector);
  if (!element) {
    return;
  }

  const newElement = element.cloneNode(true);
  element.parentNode.replaceChild(newElement, element);
  return newElement;
}

export const updateMarkerPopup = (marker, popupElement, width, height = null) => {
  const popup = marker.getPopup();
  popup.setContent(popupElement);
  popup.options.className = 'custom-popup';
  popup.options.zIndex = 1000;
  popup.options.minHeight = height;
  popup.options.maxWidth = width;
  popup.options.minWidth = width;
  popup.update();
}